import React, { Component, ReactNode } from 'react';
import { logRumError } from '../../lib/rum/rumErrorLogger';
import { getRumClient } from '../../lib/rum/rumClient';
import ServerErrorPage from '../ErrorPage/ServerErrorPage';

interface ErrorInfo {
    message: string;
    statusCode: number;
    stack?: string;
    timestamp?: string;
    requestId?: string;
}

interface ClientErrorBoundaryState {
    hasError: boolean;
    error: ErrorInfo | null;
}

interface ClientErrorBoundaryProps {
    children: ReactNode;
}

class ClientErrorBoundary extends Component<ClientErrorBoundaryProps, ClientErrorBoundaryState> {
    constructor(props: ClientErrorBoundaryProps) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
        };
    }

    static getDerivedStateFromError(error: Error): ClientErrorBoundaryState {
        // Generate a unique request ID for error tracking
        const generateRequestId = (): string => {
            return `client_req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        };

        const isDevelopment = process.env.NODE_ENV !== 'production';

        const errorInfo: ErrorInfo = {
            message: error.message || 'An unexpected client-side error occurred',
            statusCode: 500,
            timestamp: new Date().toISOString(),
            requestId: generateRequestId(),
            // Only include stack traces in development
            stack: isDevelopment ? error.stack : undefined,
        };

        if (isDevelopment) {
            console.error('🚨 Client-side error caught by ErrorBoundary:', {
                message: error.message,
                stack: error.stack,
                timestamp: errorInfo.timestamp,
                requestId: errorInfo.requestId,
            });
        }

        return {
            hasError: true,
            error: errorInfo,
        };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        // Preserve the native type, message, and stack for Datadog grouping and source maps,
        // matching mobile-app-platform's render boundary. Feature paths that handle user-derived
        // data sanitize their errors before reporting them; this generic boundary adds no props,
        // component stack, or URL to RUM context.
        logRumError(getRumClient(), error, {
            isPageFailure: true,
        });

        if (process.env.NODE_ENV !== 'production') {
            console.error('🚨 Client-side Error Context:', {
                error: {
                    message: error.message,
                    stack: error.stack,
                },
                errorInfo: {
                    componentStack: errorInfo.componentStack,
                },
                url: typeof window !== 'undefined' ? window.location.href : 'unknown',
                userAgent: typeof window !== 'undefined' ? navigator.userAgent : 'unknown',
            });
        }
    }

    render(): ReactNode {
        if (this.state.hasError && this.state.error) {
            const isDevelopment = process.env.NODE_ENV !== 'production';

            // Render the same error page component used for server-side errors
            return (
                <ServerErrorPage
                    error={this.state.error}
                    isDevelopment={isDevelopment}
                    onRetry={this.resetErrorBoundary}
                />
            );
        }

        return this.props.children;
    }

    // Method to reset the error boundary (useful for "Try Again" functionality)
    resetErrorBoundary = (): void => {
        this.setState({
            hasError: false,
            error: null,
        });
    };
}

export default ClientErrorBoundary;
